import { addYears } from 'date-fns'
import format from 'date-fns/format'
import React, { useEffect, useMemo, useState } from 'react'
import Plotly from 'plotly.js-basic-dist-min'
import { useInterval } from 'react-use'
import styled, { useTheme } from 'styled-components'
import { formatAsBrNumber } from '~/prix'
import useAction from '~/prix/react/hooks/action'
import { parseDate, stringifyDate } from '~/prix/types/date'
import { formatDateTime } from '~/prix/types/dateTime'
import {
  generateIncidenceDates,
  timeTypeForCurrentOnly,
  timeTypeForIncidenceRealtime,
  timeTypeForIncidenceRealtimePrediction,
  timeTypeForPrevalence,
} from '~/prix/types/presets/time'
import normalizeDateSequenceItems from '~/prix/utils/normalizeDateSequenceItems'
import actionDefinitions from '../actionDefinitions'
import { Layer, TimeIncidence, TimePrevalence } from '../variable/spaceTime.types'
import Modal from '~/prix/react/components/modal'
import Handle from '~/prix/react/components/handle'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons'
import { ButtonLink, ButtonLinkAsSimpleText } from '~/prix/react/components/button'
import AlertIcon from '~/components/icons/ui/16px_alert.svg'
import { ReportWithLayer } from './reports'
import DateField from '~/prix/react/components/form/inputs/dateTimeField'
import { Variable } from '../variable/variables.typing'
import Plot from '~/prix/react/components/plot'
import PulseLoaderComponent from '~/prix/react/components/pulseLoader'

type TimeSeriesItem = {
  dateTime: string
  value: number | string | null
  prediction: number | string | null
  alert?: boolean
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: 80vh;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    max-height: 70vh;
  }

  .top {
    display: flex;
    flex: 0 0 auto;
    justify-content: center;
    align-items: center;
    position: relative;
    background-color: ${({ theme }) => theme.colors.lightGrey};
    border-radius: 8px;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    padding: 1rem;

    input {
      border: none;
      border-radius: 8px;
      padding: 1rem;
    }

    .span {
      margin: 0 1rem;

      @media (max-width: 575px) {
        display: none;
      }
    }

    .date-input-wrapper {
      display: flex;
      flex-direction: row;
      padding-top: 12px;
      width: fit-content;
      gap: 12px;
      justify-content: center;
      align-items: center;

      .title {
        @media (min-width: 575px) {
          display: none;
        }
        @media (max-width: 575px) {
          margin: 0 3px 2px 0;
        }
      }

      @media (max-width: 575px) {
        flex: 1;
        gap: unset;
        flex-direction: column;
        align-items: flex-start;

        input {
          margin-bottom: 7px;
        }
      }
    }
  }

  .bottom {
    display: flex;
    position: relative;
    width: 100%;
    height: 260px;
    background-color: ${({ theme }) => theme.colors.lightGrey};
    border-radius: 8px;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    padding: 10px 56px;

    @media (max-width: 575px) {
      padding: 10px 28px;
      max-height: 220px;
    }
  }

  .children-options {
    flex: 1;
    overflow-x: hidden;

    overflow-y: scroll;

    .children-option {
      display: flex;
      position: relative;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      padding: 1rem;
      cursor: pointer;

      &:not(:last-child)::before {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        display: block;
        margin-left: 1rem;
        border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
        width: 100%;
      }
      .active {
        background-color: ${({ theme }) => theme.colors.lightGrey};
        cursor: not-allowed;
      }
    }
  }
`

const ButtonContainer = styled.div`
  display: flex;
  padding: 10px;
  justify-content: right;
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    align-items: flex-start;
    flex-wrap: wrap;
  }
`
const LastButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    margin-bottom: 10px;
  }
`

const ArticleItems = styled.article`
  display: flex;
  position: relative;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1rem;

  &:not(:last-child)::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    display: block;
    margin-left: 1rem;
    border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
    width: 100%;
  }

  &:hover {
    background-color: ${({ theme }) => theme.colors.lightGrey};

    &:last-child {
      border-bottom-left-radius: 8px;
      border-bottom-right-radius: 8px;
    }
  }

  .title {
    display: flex;
    align-items: center;
    font-weight: bold;
    gap: 4px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    flex-direction: column;
    align-items: flex-start;

    .right {
      margin-top: 1rem;
      justify-content: flex-start;
    }
  }
`

const AlertWrapper = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  gap: 1rem;
  text-align: center;
`

interface ViewSelectModalTimeProps {
  layer: Layer
  isOpen: boolean
  showPreviewData?: boolean
  showAnalyseTimeButton?: boolean
  onClose: () => void
  onChangeLayer: (layer: Layer | ((oldLayer: Layer) => Layer)) => void
  onChangeReport: (report: ReportWithLayer | null) => void
}

export default function ViewSelectModalTime({
  layer,
  isOpen,
  showPreviewData = true,
  showAnalyseTimeButton = true,
  onClose,
  onChangeLayer,
  onChangeReport,
}: ViewSelectModalTimeProps) {
  const theme = useTheme()
  const [now, setNow] = useState(() => formatDateTime(new Date()))
  const [currentLayer, setCurrentLayer] = useState<Layer>(layer)
  const [startDate, setStartDate] = useState<string | null>(null)
  const [endDate, setEndDate] = useState<string | null>(null)

  useEffect(() => {
    setCurrentLayer(layer)
  }, [layer])

  const previewDefaultTime = useMemo(() => {
    const [previewStartDate] = generateIncidenceDates.last5Years()
    const previewDateRange = generateIncidenceDates.nextYearPredicted()
    const previewEndDate = previewDateRange[1]
    return {
      type: 'customDateRangePredicted' as const,
      title: 'Preview completo',
      startDate: stringifyDate(previewStartDate),
      endDate: stringifyDate(previewEndDate),
      groupType: 'daily' as const,
    }
  }, [])

  const actionDefinition = useMemo(() => {
    const selectedActionDefinition = actionDefinitions.find(
      actionDefinition =>
        actionDefinition.key === (currentLayer.variable as Variable)?.actionDefinitionKey,
    )
    if (!selectedActionDefinition) {
      throw new Error(`Action definition not found for variable ${currentLayer.variable.key}`)
    }

    return selectedActionDefinition
  }, [currentLayer.variable])

  const commonActionInput = useMemo(() => {
    return {
      variable: currentLayer.variable,
      filter: (currentLayer.variable as Variable)?.filter,
      highlight: (currentLayer.variable as Variable)?.highlight,
      space: {
        delimitationType: currentLayer.space.delimitationType,
        delimitationId: (
          currentLayer.space as {
            delimitationId: number
          }
        ).delimitationId,
      },
      time: {
        ...currentLayer.time,
        groupType: 'daily',
      },
      analysis: {
        spaceAnalysis: (currentLayer.variable as Variable)?.spaceAnalysis,
        timeSeries: (currentLayer.variable as Variable)?.timeSeries,
        include: {
          items: true,
          prediction: 'auto',
          alerts: false,
          insights: false,
          components: false,
        },
      },
    }
  }, [currentLayer.space, currentLayer.variable])

  const quickSearchTime = useMemo(() => {
    return {
      type: 'customDateRange' as const,
      title: 'Busca rápida',
      startDate: previewDefaultTime.startDate,
      endDate: stringifyDate(new Date()),
      groupType: 'monthly' as const,
    }
  }, [previewDefaultTime.startDate])

  const quickSearchInput = useMemo(() => {
    return {
      ...commonActionInput,
      time: quickSearchTime,
    }
  }, [commonActionInput, quickSearchTime])

  const quickSearchResult = useAction(
    actionDefinition,
    quickSearchInput,
    [actionDefinition, quickSearchInput],
    {
      autoLoad: isOpen && currentLayer.variable.timeType === 'incidence',
      staleWhileRevalidate: false,
    },
  )

  const dynamicPreviewStartDate = useMemo(() => {
    if (!quickSearchResult.result?.items?.length) {
      return previewDefaultTime.startDate
    }

    const firstPositiveItem = quickSearchResult.result.items.find((item: TimeSeriesItem) => {
      const rawValue = item.value ?? item.prediction

      if (rawValue === null || rawValue === undefined) {
        return false
      }

      const numericValue = Number(rawValue)

      if (!Number.isFinite(numericValue)) {
        return false
      }

      return numericValue > 0
    })

    if (!firstPositiveItem?.dateTime) {
      return previewDefaultTime.startDate
    }

    return stringifyDate(new Date(firstPositiveItem.dateTime))
  }, [previewDefaultTime.startDate, quickSearchResult.result?.items])

  const previewTime = useMemo(() => {
    return {
      type: 'customDateRangePredicted' as const,
      title: 'Preview completo',
      startDate: dynamicPreviewStartDate,
      endDate: previewDefaultTime.endDate,
      groupType: 'daily' as const,
    }
  }, [dynamicPreviewStartDate, previewDefaultTime.endDate])

  const previewInput = useMemo(() => {
    return {
      ...commonActionInput,
      time: previewTime,
    }
  }, [commonActionInput, previewTime])

  const { timeType } = currentLayer.variable

  const hasQuickSearchResponse = Boolean(quickSearchResult.result || quickSearchResult.error)
  const hasQuickSearchCompleted = typeof quickSearchResult.result !== 'undefined'

  const previewResult = useAction(
    actionDefinition,
    previewInput,
    [actionDefinition, previewInput],
    {
      autoLoad:
        isOpen &&
        currentLayer.variable.timeType === 'incidence' &&
        showPreviewData &&
        hasQuickSearchCompleted &&
        !quickSearchResult.error &&
        !quickSearchResult.isLoading,
      staleWhileRevalidate: false,
    },
  )

  const isPreviewRequestInFlight = quickSearchResult.isLoading || previewResult.isLoading
  const previewError = quickSearchResult.error || previewResult.error

  const previewItems = useMemo(() => {
    if (isPreviewRequestInFlight || previewError || !previewResult.result?.items) {
      return []
    }

    return normalizeDateSequenceItems<any>(
      previewResult.result.items.map((item: TimeSeriesItem) => ({
        dateTime: item.dateTime,
        value: item.value ?? item.prediction,
        alert: item.alert,
      })),
      dateString => ({ dateTime: dateString, value: 0, alert: false }),
      {
        startDate: parseDate(previewTime.startDate),
        endDate: parseDate(previewTime.endDate),
        groupType: 'daily',
      },
    )
  }, [
    isPreviewRequestInFlight,
    previewError,
    previewResult.result?.items,
    previewTime.endDate,
    previewTime.startDate,
  ])

  const [previewPlot, hasPreviewData] = useMemo(() => {
    if (!previewItems.length) {
      return [null, false] as const
    }

    let displayItems = previewItems
    const isPredictionTimeFilter = String(currentLayer.time.type).includes(
      'customDateRangePredicted',
    )

    const startDateLayer =
      'startDate' in currentLayer.time ? currentLayer.time.startDate : undefined
    const endDateLayer = 'endDate' in currentLayer.time ? currentLayer.time.endDate : undefined

    if (!isPredictionTimeFilter && endDateLayer) {
      displayItems = displayItems.filter(item => item.dateTime <= endDateLayer)
    }

    if (startDateLayer && endDateLayer) {
      const startDateObj = parseDate(startDateLayer)
      const endDateObj = parseDate(endDateLayer)

      const diffInDays = Math.floor(
        (endDateObj.getTime() - startDateObj.getTime()) / (1000 * 3600 * 24),
      )

      if (diffInDays > 60) {
        const minPoints = 48
        const interval = Math.floor(displayItems.length / minPoints)

        if (interval > 1) {
          displayItems = displayItems.filter((_, index) => index % interval === 0)
        }
      }
    }

    const maxPlotPoints = 480
    if (displayItems.length > maxPlotPoints) {
      const interval = Math.ceil(displayItems.length / maxPlotPoints)
      displayItems = displayItems.filter((_, index) => index % interval === 0)
    }

    const dates = displayItems.map(item => item.dateTime)
    const values = displayItems.map(item =>
      typeof item.value === 'number' ? item.value : item.value === null ? null : Number(item.value),
    )
    const hasSelectedRange = Boolean(startDate && endDate)

    const formattedValues = values.map(value => (value === null ? '0' : formatAsBrNumber(value)))

    const hoverTemplate = 'Data: %{x|%d/%m/%Y}<br>Valor: %{customdata}<extra></extra>'

    const data: Plotly.Data[] = [
      {
        type: 'scatter',
        mode: 'lines',
        x: dates,
        y: values,
        customdata: formattedValues,
        line: {
          color: theme.colors.primary,
          width: 2,
        },
        hovertemplate: hoverTemplate,
        showlegend: false,
      },
    ]

    const layout: Partial<Plotly.Layout> = {
      height: 196,
      margin: { l: 24, r: 6, t: 12, b: 12, pad: 0 },
      paper_bgcolor: 'transparent',
      plot_bgcolor: 'transparent',
      showlegend: false,
      hovermode: 'closest',
      hoverlabel: {
        bgcolor: '#ffffff',
        bordercolor: '#d1d5db',
        align: 'left',
        font: { family: 'Figtree, Arial, sans-serif', size: 14, color: '#111827' },
      },
      xaxis: {
        type: 'date',
        tickformat: '%d/%m/%Y',
        automargin: true,
        showgrid: false,
        zeroline: false,
        tickfont: {
          family: 'Figtree, Arial, sans-serif',
          size: 12,
          color: '#374151',
        },
      },
      yaxis: {
        tickformat: ',.0f',
        automargin: true,
        showgrid: true,
        gridcolor: 'rgba(0, 0, 0, 0.08)',
        zeroline: false,
        tickfont: {
          family: 'Figtree, Arial, sans-serif',
          size: 12,
          color: '#374151',
        },
      },
    }

    if (hasSelectedRange && startDate && endDate) {
      layout.shapes = [
        {
          type: 'rect',
          xref: 'x',
          yref: 'paper',
          x0: startDate,
          x1: endDate,
          y0: 0,
          y1: 1,
          fillcolor: 'rgba(245, 112, 11, 0.11)',
          line: {
            width: 0,
          },
        } as unknown as Plotly.Shape,
        {
          type: 'line',
          xref: 'x',
          yref: 'paper',
          x0: startDate,
          x1: startDate,
          y0: 0,
          y1: 1,
          line: {
            color: '#fd5542',
            width: 1,
            dash: 'dot',
          },
        } as unknown as Plotly.Shape,
        {
          type: 'line',
          xref: 'x',
          yref: 'paper',
          x0: endDate,
          x1: endDate,
          y0: 0,
          y1: 1,
          line: {
            color: '#fd5542',
            width: 1,
            dash: 'dot',
          },
        } as unknown as Plotly.Shape,
      ]
    }

    return [
      {
        data,
        layout,
      },
      values.some(value => value !== null),
    ] as const
  }, [currentLayer.time, endDate, previewItems, startDate, theme.colors.primary])

  const isPreviewLoading =
    isPreviewRequestInFlight || (!previewError && hasQuickSearchResponse && !previewResult.result)
  const shouldShowEmptyPreviewState =
    !isPreviewLoading && !previewError && !hasPreviewData && Boolean(previewResult.result)

  useInterval(
    () => {
      setNow(formatDateTime(new Date()))
    },
    isOpen && timeType === 'currentOnly' ? 100 : null,
  )

  useEffect(() => {
    if (timeType !== 'incidence') {
      setStartDate(null)
      setEndDate(null)
      return
    }

    setStartDate(
      (currentLayer.time as TimeIncidence).startDate
        ? (currentLayer.time as TimeIncidence).startDate!
        : null,
    )
    setEndDate(
      (currentLayer.time as TimeIncidence).endDate
        ? (currentLayer.time as TimeIncidence).endDate!
        : null,
    )

    return
  }, [timeType, currentLayer])

  const shape = {
    prevalence: timeTypeForPrevalence.keysAndLabels,
    incidence: {
      ...timeTypeForIncidenceRealtime.keysAndLabels,
      ...timeTypeForIncidenceRealtimePrediction.keysAndLabels,
    },
    currentOnly: timeTypeForCurrentOnly.keysAndLabels,
  }[timeType]

  const entries = Object.entries(shape)

  const handleSelectChildrenOption = (key: string) => {
    if (currentLayer.time.type === key) return

    if (timeType === 'currentOnly') {
      return
    }
    if (timeType === 'prevalence') {
      setCurrentLayer(oldLayer => {
        const date = new Date()
        const newLayer: Layer = {
          ...oldLayer,
          time: {
            ...oldLayer.time,
            type: key as TimePrevalence['type'],
            title: shape[key],
            date,
          },
        }
        return newLayer
      })

      return
    }

    const [newStartDateObj, newEndDateObj] =
      generateIncidenceDates[key as keyof typeof generateIncidenceDates]()

    const [newStartDate, newEndDate] = [
      stringifyDate(newStartDateObj),
      stringifyDate(newEndDateObj),
    ]

    setCurrentLayer(oldLayer => {
      const newLayer: Layer = {
        ...oldLayer,
        time: {
          startDate: newStartDate,
          endDate: newEndDate,
          title: shape[key],
          type: key as TimeIncidence['type'],
        },
      }
      return newLayer
    })

    setStartDate(newStartDate)
    setEndDate(newEndDate)
  }

  const handleSelectStartDate = (startDateString: string | null) => {
    if (timeType !== 'incidence') {
      console.warn('handleSelectEndDate called with timeType', timeType)
      return
    }

    if (!startDateString) {
      setStartDate(null)
      setCurrentLayer(oldLayer => {
        const newLayer: Layer = {
          ...oldLayer,
          time: {
            ...oldLayer.time,
            title: 'Intervalo de datas personalizado',
            type: 'customDateRange',
            startDate: undefined,
            endDate: (oldLayer.time as TimeIncidence).endDate,
          },
        }
        return newLayer
      })
      return
    }

    setStartDate(startDateString)
    const parsedStartDate = startDateString

    setCurrentLayer(oldLayer => {
      const newLayer: Layer = {
        ...oldLayer,
        time: {
          ...oldLayer.time,
          title:
            (oldLayer.time as TimeIncidence).endDate! <= stringifyDate(new Date())
              ? 'Intervalo de datas personalizado'
              : 'Intervalo de datas personalizado (predição)',
          type:
            (oldLayer.time as TimeIncidence).endDate! <= stringifyDate(new Date())
              ? 'customDateRange'
              : 'customDateRangePredicted',
          startDate: parsedStartDate,
          endDate: (oldLayer.time as TimeIncidence).endDate,
        },
      }
      return newLayer
    })
  }

  const handleSelectEndDate = (endDateString: string | null) => {
    if (timeType !== 'incidence') {
      console.warn('handleSelectEndDate called with timeType', timeType)
      return
    }

    if (!endDateString) {
      setEndDate(null)
      setCurrentLayer(oldLayer => {
        const newLayer: Layer = {
          ...oldLayer,
          time: {
            ...oldLayer.time,
            title: 'Intervalo de datas personalizado',
            type: 'customDateRange',
            startDate: (oldLayer.time as TimeIncidence).startDate,
            endDate: undefined,
          },
        }
        return newLayer
      })
      return
    }

    setEndDate(endDateString)
    const endDate = endDateString
    setCurrentLayer(oldLayer => {
      const newLayer: Layer = {
        ...oldLayer,
        time: {
          ...oldLayer.time,
          title:
            endDate <= stringifyDate(new Date())
              ? 'Intervalo de datas personalizado'
              : 'Intervalo de datas personalizado (predição)',
          type:
            endDate <= stringifyDate(new Date()) ? 'customDateRange' : 'customDateRangePredicted',
          startDate: (oldLayer.time as TimeIncidence).startDate,
          endDate: endDate,
        },
      }
      return newLayer
    })
  }

  const handleCancel = () => {
    setCurrentLayer(layer)
    onClose()
  }
  const handleAnalyze = () => {
    if (!showAnalyseTimeButton) return

    onChangeReport({
      layer: currentLayer,
      reportKey: 'timeAnalysis',
      reportPayload: {
        spaceDelimitationType: currentLayer.space.delimitationType,
        spaceDelimitationId: (
          currentLayer.space as {
            delimitationId: number
          }
        ).delimitationId,
      },
    })
    onClose()
  }
  const handleConfirm = () => {
    onChangeLayer(currentLayer)
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} modalStyle='sentinel'>
      <Handle>
        <Wrapper>
          <div className='top'>
            {timeType === 'currentOnly' && now}
            {timeType === 'incidence' && (
              <div className='date-input-wrapper'>
                <span className='title'>Início</span>
                <DateField
                  id='start-day-input'
                  value={startDate}
                  placeholder='De'
                  onDirectChange={date =>
                    handleSelectStartDate(date ? format(date, 'yyyy-MM-dd') : null)
                  }
                  meta={{
                    maxDate: addYears(new Date(), 2),
                  }}
                  isRequired
                />
                <span className='span'>até</span>
                <span className='title'>Fim</span>
                <DateField
                  id='final-day-input'
                  value={endDate}
                  placeholder='Até'
                  onDirectChange={date =>
                    handleSelectEndDate(date ? format(date, 'yyyy-MM-dd') : null)
                  }
                  meta={{
                    maxDate: addYears(new Date(), 2),
                  }}
                  isRequired
                />
              </div>
            )}
          </div>
          <div className='children-options'>
            {entries.map(([key, name]) => {
              return (
                <ArticleItems
                  key={key}
                  className={`children-option ${currentLayer.time.type === key ? 'active' : ''}`}
                  role='button'
                  onClick={isPreviewLoading ? () => {} : () => handleSelectChildrenOption(key)}
                  style={{ cursor: isPreviewLoading ? 'wait' : 'pointer' }}
                >
                  <div className='title'>
                    {currentLayer.time.type === key && (
                      <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#0C2340' }} />
                    )}
                    {name}
                  </div>
                </ArticleItems>
              )
            })}
          </div>
          {timeType !== 'currentOnly' &&
            showPreviewData &&
            (!startDate || !endDate ? (
              <AlertWrapper>
                <AlertIcon fill={'#f1c40f'} width={18} height={18} />
                <p>{`A data ${!startDate ? 'inicial' : 'final'} não pode estar vazia`}</p>
              </AlertWrapper>
            ) : parseDate(startDate) > parseDate(endDate) ? (
              <AlertWrapper>
                <AlertIcon fill={'#f1c40f'} width={18} height={18} />
                <p>A data inicial não pode ser maior que a data final</p>
              </AlertWrapper>
            ) : hasPreviewData ||
              isPreviewLoading ||
              Boolean(previewError) ||
              shouldShowEmptyPreviewState ? (
              <div className='bottom'>
                {isPreviewLoading ? (
                  <PulseLoaderComponent />
                ) : previewError ? (
                  <AlertWrapper>
                    <AlertIcon fill={'#000000'} width={18} height={18} />
                    <p>Não foi possível carregar a pré-visualização para esta variável</p>
                  </AlertWrapper>
                ) : shouldShowEmptyPreviewState ? (
                  <AlertWrapper>
                    <AlertIcon fill={'#000000'} width={18} height={18} />
                    <p>Nenhum dado disponível para a pré-visualização neste período</p>
                  </AlertWrapper>
                ) : previewPlot ? (
                  <Plot data={previewPlot.data} layout={previewPlot.layout} />
                ) : null}
              </div>
            ) : null)}
          <ButtonContainer>
            <ButtonLinkAsSimpleText style={{ marginRight: '7px' }} onGo={handleCancel}>
              Cancelar
            </ButtonLinkAsSimpleText>
            {timeType === 'incidence' && showAnalyseTimeButton && (
              <ButtonLinkAsSimpleText style={{ marginRight: '7px' }} onGo={handleAnalyze}>
                Analisar tempo
              </ButtonLinkAsSimpleText>
            )}
            <LastButton>
              <ButtonLink onGo={handleConfirm}>Confirmar</ButtonLink>
            </LastButton>
          </ButtonContainer>
        </Wrapper>
      </Handle>
    </Modal>
  )
}
